/**
 * Tweaks: UI カスタマイズ設定（accent / theme / density / competitorOverlay / aiComments）
 * localStorage に永続化。document.documentElement の data-* 属性に反映してCSS変数を切替。
 */
import { useSyncExternalStore } from "react";

export type Tweaks = {
  accent: "green" | "ink";
  theme: "light" | "dark";
  density: "medium" | "high";
  competitorOverlay: "calm" | "alert" | "hidden";
  aiComments: boolean;
};

const STORAGE_KEY = "axis-tweaks";

const DEFAULT_TWEAKS: Tweaks = {
  accent: "green",
  theme: "light",
  density: "medium",
  competitorOverlay: "calm",
  aiComments: true,
};

function readFromStorage(): Tweaks {
  if (typeof window === "undefined") return DEFAULT_TWEAKS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_TWEAKS;
    return { ...DEFAULT_TWEAKS, ...(JSON.parse(raw) as Partial<Tweaks>) };
  } catch {
    return DEFAULT_TWEAKS;
  }
}

function writeToStorage(tweaks: Tweaks): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks));
  } catch {
    // quota exceeded / private mode — ignore
  }
}

export function applyTweaksToDom(tweaks: Tweaks): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.setAttribute("data-accent", tweaks.accent);
  root.setAttribute("data-theme", tweaks.theme);
  root.setAttribute("data-density", tweaks.density);
  root.setAttribute("data-competitor-overlay", tweaks.competitorOverlay);
  root.setAttribute("data-ai-comments", String(tweaks.aiComments));
}

const listeners = new Set<() => void>();
let current: Tweaks = readFromStorage();

function emit(): void {
  for (const l of listeners) l();
}

export function getTweaks(): Tweaks {
  return current;
}

export function setTweaks(partial: Partial<Tweaks>): void {
  current = { ...current, ...partial };
  writeToStorage(current);
  applyTweaksToDom(current);
  emit();
}

export function useTweaks(): [Tweaks, (p: Partial<Tweaks>) => void] {
  const tweaks = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    () => current,
    () => DEFAULT_TWEAKS,
  );
  return [tweaks, setTweaks];
}
